
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { useTrainingProgress } from "@/hooks/useTrainingProgress";
import { ResponseItem } from "@/components/resource/types";

export function useFreeResponse({ 
  resourceId, 
  fileId, 
  onComplete 
}: { 
  resourceId: string;
  fileId: string;
  onComplete?: () => void;
}) {
  const [questions, setQuestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState<any | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [hasMarkedComplete, setHasMarkedComplete] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();
  const { session } = useSession();
  const { data: profile } = useProfile(session?.user?.id);
  const isAdmin = profile?.role === 'admin';
  
  const { 
    isCompleted,
    completedAt,
    markAsCompleted 
  } = useTrainingProgress(resourceId, fileId);
  
  const isMountedRef = useRef(true);
  
  // Initialize the component
  useEffect(() => {
    isMountedRef.current = true;
    
    // Only fetch data if not already initialized
    if (!isInitialized) {
      fetchData();
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [session, fileId, resourceId, isInitialized]);
  
  // Check for completed attempts and mark as completed if needed
  useEffect(() => {
    if (attempt?.completed_at && !isCompleted && !hasMarkedComplete && session?.user) {
      const handleCompletion = async () => {
        console.log('Found completed assessment, marking as completed in training progress');
        setHasMarkedComplete(true);
        await markAsCompleted();
        if (onComplete && isMountedRef.current) {
          onComplete();
        }
      };
      
      handleCompletion();
    }
  }, [attempt, isCompleted, hasMarkedComplete, markAsCompleted, onComplete, session]);
  
  const fetchData = async () => {
    if ((!session?.user && !isAdmin) || !fileId) return;
    
    try {
      if (isMountedRef.current) setIsLoading(true);
      
      const { data: questionsData, error: questionsError } = await supabase
        .from('free_response_questions')
        .select('*')
        .eq('file_id', fileId)
        .order('order_index');
      
      if (questionsError) throw questionsError;
      
      if (isMountedRef.current) setQuestions(questionsData || []);
      
      if (!isAdmin && session?.user) {
        const { data: attemptData, error: attemptError } = await supabase
          .from('free_response_attempts')
          .select('*')
          .eq('file_id', fileId)
          .eq('trainee_id', session.user.id)
          .order('created_at', { ascending: false })
          .maybeSingle();
        
        if (attemptError) throw attemptError;
        
        if (attemptData) {
          const responseArray: ResponseItem[] = [];
          
          if (attemptData.responses && Array.isArray(attemptData.responses)) {
            for (const item of attemptData.responses) {
              if (item && 
                  typeof item === 'object' && 
                  'question_id' in item && 
                  'answer' in item &&
                  typeof item.question_id === 'string' &&
                  typeof item.answer === 'string') {
                responseArray.push({
                  question_id: item.question_id,
                  answer: item.answer
                });
              }
            }
          }
          
          if (isMountedRef.current) {
            setAttempt(attemptData);
            setAttemptId(attemptData.id);
            
            const responseMap = responseArray.reduce((acc, item) => {
              acc[item.question_id] = item.answer;
              return acc;
            }, {} as Record<string, string>);
            
            setResponses(responseMap);
          }
        } else if (isMountedRef.current) {
          // Create a new attempt record if none exists
          const { data: newAttempt, error: createError } = await supabase
            .from('free_response_attempts')
            .insert({
              file_id: fileId,
              resource_id: resourceId,
              trainee_id: session.user.id,
              responses: []
            })
            .select()
            .single();
          
          if (createError) throw createError;
          
          if (isMountedRef.current) setAttemptId(newAttempt.id);
        }
      }
    } catch (error) {
      console.error('Error fetching free response data:', error);
      if (isMountedRef.current) {
        toast({
          title: "Error",
          description: "Failed to load questions",
          variant: "destructive",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setIsInitialized(true);
      }
    }
  };
  
  const handleResponseChange = (questionId: string, value: string) => {
    if (isAdmin || isCompleted) return;
    
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  
  const handleSubmit = async () => {
    if (!session?.user || !attemptId || isAdmin) return Promise.resolve();
    
    try {
      setIsSubmitting(true);
      
      const allQuestionsAnswered = questions.every(q => !!responses[q.id] && responses[q.id].trim() !== '');
      
      if (!allQuestionsAnswered) {
        toast({
          title: "Incomplete Responses",
          description: "Please answer all questions before submitting",
          variant: "destructive",
        });
        return Promise.resolve();
      }
      
      const formattedResponses = questions.map(question => ({
        question_id: question.id,
        answer: responses[question.id] || ''
      }));
      
      const { error: updateError } = await supabase
        .from('free_response_attempts')
        .update({
          responses: formattedResponses,
          completed_at: new Date().toISOString()
        })
        .eq('id', attemptId);
      
      if (updateError) throw updateError;
      
      // Set flag to prevent duplicate calls
      setHasMarkedComplete(true);
      await markAsCompleted();
      
      toast({
        title: "Responses Submitted",
        description: "Your responses have been saved successfully",
      });
      
      if (onComplete) onComplete();
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error submitting responses:', error);
      toast({
        title: "Error",
        description: "Failed to submit responses",
        variant: "destructive",
      });
      return Promise.reject(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    questions,
    isLoading,
    isSubmitting,
    responses,
    attempt,
    isCompleted,
    completedAt,
    isAdmin,
    handleResponseChange,
    handleSubmit
  };
}
